import React, { useMemo } from 'react';
import { IKanbanProviderProps } from './HBKanban.types';
import { HBKanbanContext, HBKanbanContextValue } from './HBKanbanContext';
import { useKanban } from './useKanban';

/**
 * Provider component for HBKanban context
 * Manages the kanban state and provides it to child components
 */
export const HBKanbanProvider = <TItemData = any, TColumnData = any>({
    children,
    items,
    columns,
    groups,
    dragDropHandlers,
    shouldRejectDrop,
    renderProps,
    onCardClick,
    testIdName,
    infiniteScroll,
    isSelectable = false,
    containerRef,
    virtualization,
    horizontalPadding
}: IKanbanProviderProps<TItemData, TColumnData>) => {
    const {
        handleDragStart,
        handleDragOver,
        handleDragEnd,
        getCardById,
        getActiveCard,
        canDropInColumn,
        getSourceColumnForCard,
        activeIds,
        overId,
        draggingOverNonDroppable,
        internalItems,
        columnCounts,
        isInSelectableState,
        isCardSelected,
        onToggleSelect,
        onLoadMore,
        isLoadingColumn,
        removeCard,
        columnContentRefs,
        registerColumnContentRef,
        columnRefs,
        registerColumnRef,
        columnRefsVersion
    } = useKanban<TItemData>({
        items,
        columns,
        dragDropHandlers,
        shouldRejectDrop,
        infiniteScroll,
        isSelectable
    });

    // Memoize the context value to prevent unnecessary re-renders
    const value = useMemo<HBKanbanContextValue<TItemData, TColumnData>>(
        () => ({
            columns,
            groups,
            items: internalItems,
            renderProps,
            onCardClick,
            isCardSelected,
            onToggleSelect,
            isInSelectableState,
            testIdName,
            infiniteScrollProps: infiniteScroll,
            onLoadMore,
            isLoadingColumn,
            columnCounts,

            // Drag handlers
            handleDragStart,
            handleDragOver,
            handleDragEnd,

            // Drag state
            activeIds,
            overId,
            draggingOverNonDroppable,

            // Helper functions
            getCardById,
            getActiveCard,
            canDropInColumn,
            getSourceColumnForCard,

            // Card deletion
            removeCard,

            // Container ref
            containerRef,
            // Column content refs
            columnContentRefs,
            registerColumnContentRef,
            // Column element refs
            columnRefs,
            registerColumnRef,
            columnRefsVersion,
            // Virtualization props
            virtualization,
            horizontalPadding
        }),
        [
            columns,
            groups,
            internalItems,
            renderProps,
            onCardClick,
            isCardSelected,
            onToggleSelect,
            isInSelectableState,
            testIdName,
            infiniteScroll,
            onLoadMore,
            isLoadingColumn,
            columnCounts,
            handleDragStart,
            handleDragOver,
            handleDragEnd,
            activeIds,
            overId,
            draggingOverNonDroppable,
            getCardById,
            getActiveCard,
            canDropInColumn,
            getSourceColumnForCard,
            containerRef,
            columnContentRefs,
            registerColumnContentRef,
            columnRefs,
            registerColumnRef,
            columnRefsVersion,
            virtualization,
            removeCard,
            horizontalPadding
        ]
    );

    return (
        <HBKanbanContext.Provider value={value}>
            {children}
        </HBKanbanContext.Provider>
    );
};

export default HBKanbanProvider;
